import moment from "moment";
import { getBuildingCurrency } from "@/utilities/countrySpecific";

/**
 * Transform data for CSV export
 * @param {Array} data - Raw cancellation data
 * @param {Object} options - Export options
 * @returns {Array} - Transformed data for CSV export
 */
export const prepareCancellationCsvData = (data, options = {}) => {
  const {
    includeIds = false,
    dateFormat = "DD/MM/YYYY",
    currencySymbol = getBuildingCurrency(),
  } = options;

  return data.map((item, index) => {
    const csvRow = {
      // Basic information
      Numéro: index + 1,
      "Numéro de série": item.serial || "--",
      "Nom client": item.clientName || "--",
      Type: item.type || "--",

      // Financial information
      "Montant TTC": item.money
        ? `${Number(item.money).toFixed(2)} ${currencySymbol}`
        : "0.00",
      Facturé: item.bill ? "Oui" : "Non",

      // Date information
      "Date annulation": item.date
        ? moment(item.date).format(dateFormat)
        : "--",
      "Date création": item.createdAt
        ? moment(item.createdAt).format(dateFormat)
        : "--",

      // Payment information
      "Types de règlement": Array.isArray(item.allTypeReglement)
        ? item.allTypeReglement.join(", ")
        : "--",

      // User information
      "Annulé par": item.userName || "--",

      // Additional information
      Note: item.note && item.note !== "--" ? item.note : "--",
    };

    // Add IDs if requested
    if (includeIds) {
      csvRow["ID"] = item._id || "--";
      csvRow["Année scolaire"] = item.schoolarYear?._id || "--";
    }

    return csvRow;
  });
};

/**
 * Prepare CSV data with custom labels for vue-json-csv
 * @param {Array} data - Raw cancellation data
 * @param {Object} customLabels - Custom column labels
 * @returns {Object} - Object with data and labels for vue-json-csv
 */
export const prepareCsvExport = (data, customLabels = {}) => {
    // Default labels
    const defaultLabels = {
        serial: "Numéro de série",
        clientName: "Nom client",
        type: "Type",
        money: "Montant TTC",
        bill: "Facturé",
        date: "Date annulation",
        createdAt: "Date création",
        allTypeReglement: "Types de règlement",
        userName: "Annulé par",
        note: "Note"
    };

    // Merge with custom labels
    const labels = { ...defaultLabels, ...customLabels };

    // Transform data
    const csvData = data.map((item) => ({
        serial: item.serial || "--",
        clientName: item.clientName || "--",
        type: item.type || "--",
        money: item.money ? Number(item.money).toFixed(2) : "0.00",
        bill: item.bill ? "Oui" : "Non",
        date: item.date ? moment(item.date).format("DD/MM/YYYY") : "--",
        createdAt: item.createdAt ? moment(item.createdAt).format("DD/MM/YYYY") : "--",
        allTypeReglement: Array.isArray(item.allTypeReglement)
            ? item.allTypeReglement.join(", ")
            : "--",
        userName: item.userName || "--",
        note: item.note && item.note !== "--" ? item.note : "--"
    }));

    return {
        data: csvData,
        labels: labels
    };
};

/**
 * Generate summary statistics for CSV export
 * @param {Array} data - Cancellation data
 * @returns {Object} - Summary statistics
 */
export const generateCsvSummary = (data) => {
    const summary = {
        totalItems: data.length,
        totalAmount: 0,
        byType: {},
        byUser: {},
        dateRange: { earliest: null, latest: null }
    };

    data.forEach((item) => {
        // Calculate total amount
        summary.totalAmount += Number(item.money || 0);

        // Count by type
        const type = item.type || "Unknown";
        summary.byType[type] = (summary.byType[type] || 0) + 1;

        // Count by user
        const user = item.userName || "Unknown";
        summary.byUser[user] = (summary.byUser[user] || 0) + 1;

        // Track date range
        if (item.date) {
            const date = moment(item.date);
            if (!summary.dateRange.earliest || date.isBefore(summary.dateRange.earliest)) {
                summary.dateRange.earliest = date;
            }
            if (!summary.dateRange.latest || date.isAfter(summary.dateRange.latest)) {
                summary.dateRange.latest = date;
            }
        }
    });

    // Format date range
    if (summary.dateRange.earliest && summary.dateRange.latest) {
        summary.dateRange.formatted = {
            earliest: summary.dateRange.earliest.format("DD/MM/YYYY"),
            latest: summary.dateRange.latest.format("DD/MM/YYYY")
        };
    }

    return summary;
};

/**
 * Create detailed CSV export with summary
 * @param {Array} data - Cancellation data
 * @param {Object} options - Export options
 * @returns {Object} - Complete export data structure
 */
export const createDetailedCsvExport = (data, options = {}) => {
    const {
        includeSummary = true,
        includeMetadata = true,
        customLabels = {},
        filename = null
    } = options;

    const result = {
        mainData: prepareCancellationCsvData(data, options),
        filename: filename || `reglements-annules_${moment().format("YYYY-MM-DD_HH-mm")}.csv`
    };

    if (includeSummary) {
        result.summary = generateCsvSummary(data);
    }

    if (includeMetadata) {
        result.metadata = {
            exportDate: moment().format("DD/MM/YYYY HH:mm"),
            totalRecords: data.length,
            exportedBy: "System", // Could be passed as parameter
            filters: options.filters || "Aucun"
        };
    }

    return result;
};

/**
 * Download CSV file directly (alternative to vue-json-csv)
 * @param {Array} data - Cancellation data
 * @param {String} filename - File name
 * @param {Object} options - Export options
 */
export const downloadCsvFile = (data, filename, options = {}) => {
    try {
        const csvData = prepareCancellationCsvData(data, options);

        if (csvData.length === 0) {
            throw new Error("Aucune donnée à exporter");
        }

        // Get headers from first row
        const headers = Object.keys(csvData[0]);

        // Create CSV content
        let csvContent = "data:text/csv;charset=utf-8,";

        // Add BOM for proper UTF-8 encoding in Excel
        csvContent += "\uFEFF";

        // Add headers
        csvContent += headers.join(",") + "\r\n";

        // Add data rows
        csvData.forEach((row) => {
            const values = headers.map(header => {
                const value = row[header] || "";
                // Escape commas and quotes
                return typeof value === "string" && (value.includes(",") || value.includes('"'))
                    ? `"${value.replace(/"/g, '""')}"`
                    : value;
            });
            csvContent += values.join(",") + "\r\n";
        });

        // Create and trigger download
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        return {
            success: true,
            message: "CSV téléchargé avec succès",
            filename
        };

    } catch (error) {
        console.error("Error downloading CSV:", error);
        return {
            success: false,
            message: "Erreur lors du téléchargement du CSV",
            error: error.message
        };
    }
}; 